import {
	AsyncSelect,
	Table,
	Stack,
	RelativeTime,
	Text,
	Box,
	Dropdown,
	Icons,
	Component,
	Alert,
	Spinner,
	Inline,
	Button,
	Divider,
	Badge,
	RadioInput,
	TableDataCell,
	TableHeaderDataCell,
	Select,
} from "@sembark-travel/ui-base"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { Omit } from "utility-types"
import { SelectTripDestination, TTripDestination } from "../TripDestinations"
import { ICabType } from "./store"
import {
	areAdvancedFiltersAppliedDefault,
	TSearchParams,
	Search,
	useSearch,
	ListView,
} from "@sembark-travel/ui-list"
import { useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect, useId } from "react"
import {
	EmptyNumberValidator,
	Form,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions, useHasFeatureFlag } from "../Auth"
import { ShowCabCapacityInfo } from "./ShowCabCapacityInfo"
import * as Validator from "yup"

function XHR(xhr: XHRInstance) {
	return {
		async get(params?: unknown): Promise<IListResponse<ICabType>> {
			return xhr.get("/cab-types", { params }).then(({ data }) => data)
		},
		async show(id: number | string) {
			return xhr
				.get<{ data: ICabType }>(`/cab-types/${id}`)
				.then(({ data }) => data)
		},
		async destroy(id: number | string) {
			return xhr
				.delete<{ message: string }>(`/cab-types/${id}`)
				.then((resp) => resp.data)
		},
		async restore(id: number | string) {
			return xhr
				.post<{ data: ICabType }>(`/cab-types/${id}/restore`)
				.then((resp) => resp.data.data)
		},
		async setTripDestinations(
			id: number | string,
			trip_destinations: Array<TTripDestination>,
			capacity: number | null,
			capacity_child_age_limit: number | null
		) {
			return xhr
				.patch<{ data: ICabType }>(`/cab-types/${id}/trip-destinations`, {
					trip_destinations: trip_destinations?.map((t) => t.id) || [],
					capacity: capacity,
					capacity_child_age_limit: capacity_child_age_limit,
				})
				.then((resp) => resp.data)
		},
	}
}
const uploadTypeOptions = [
	{ id: "only_sheet", name: "Only Sheet" },
	{ id: "only_manual", name: "Only Manual" },
	{ id: "only_preload", name: "Only AI Generated" },
]
type TFilters = TSearchParams & {
	archived_only?: boolean
	trip_destinations?: Array<TTripDestination>
	entry_type?: (typeof uploadTypeOptions)[number]
}

type TQueryFilters = TSearchParams & {
	archived_only?: 1
	trip_destinations?: Array<string>
	entry_type?: (typeof uploadTypeOptions)[number]["id"]
}

function filtersToLocationQuery(filters: TFilters): TQueryFilters {
	const { q, archived_only, page, trip_destinations, entry_type } = filters
	const query: TQueryFilters = {}
	if (q) query.q = q
	if (page) query.page = page
	if (archived_only) query.archived_only = 1
	if (trip_destinations?.length) {
		query.trip_destinations = trip_destinations.map((s) => `${s.id}_${s.name}`)
	}
	if (entry_type) {
		query.entry_type = entry_type.id
	}
	return query
}

function queryToFilters(query: TQueryFilters): TFilters {
	const { q, page, archived_only, trip_destinations, entry_type } = query
	const filters: TFilters = {}
	if (q) filters.q = q
	if (page) filters.page = page
	if (archived_only) filters.archived_only = true
	if (trip_destinations?.length) {
		filters.trip_destinations = trip_destinations.map((s: string) => {
			const [id, ...name] = s.split("_")
			return {
				id: isNaN(parseInt(id)) ? id : parseInt(id),
				name: name.join("_"),
			}
		}) as unknown as TFilters["trip_destinations"]
	}
	if (entry_type) {
		filters.entry_type = uploadTypeOptions.find((t) => t.id === entry_type)
	}
	return filters
}

export function CabTypesList() {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToLocationQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch(query)
	useEffect(() => {
		setQuery(params)
	}, [setQuery, params])
	const { hasPermission } = useCheckPermissions()
	return (
		<Search
			initialParams={params}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			title="Cab Types"
			resetParams={{
				q: "",
				page: 1,
			}}
			Filters={Filters}
			areAdvancedFiltersApplied={(params) => {
				const { archived_only, ...otherParams } = params
				return (
					areAdvancedFiltersAppliedDefault(otherParams) ||
					Boolean(archived_only)
				)
			}}
		>
			<ListView<ICabType, TFilters>
				pageKey="cab-types"
				params={params}
				fetch={(xhr, params) =>
					XHR(xhr).get({
						...params,
						trip_destinations: params.trip_destinations?.map((t) => t.id),
						include: "created_by,updated_by",
						entry_type: params.entry_type?.id,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items, refresh, xhr }) => (
					<Table
						headers={["Name", "Created By", "Last Updated"]
							.concat(params.archived_only ? ["Archived By"] : [])
							.concat([""])}
						bordered
						hover
						responsive
						alignCols={{ [params.archived_only ? 4 : 3]: "right" }}
						rows={items.map((item) =>
							[
								<Stack gap="px">
									<Text>{item.display_name}</Text>
									{item.short_name ? (
										<Text color="muted" fontSize="sm">
											[[{item.short_name}]]
										</Text>
									) : null}
									<ShowCabCapacityInfo showEntryType cabType={item} />
								</Stack>,
								<Stack>
									<Text>{item.created_by?.name}</Text>
									{item.created_at ? (
										<Box>
											<RelativeTime
												timestamp={item.created_at}
												fontSize="sm"
												color="muted"
											/>
										</Box>
									) : null}
								</Stack>,
								item.updated_at !== item.created_at && item.updated_at ? (
									<Stack>
										<Text>{item.updated_by?.name}</Text>
										{item.updated_at ? (
											<Box>
												<RelativeTime
													timestamp={item.updated_at}
													fontSize="sm"
													color="muted"
												/>
											</Box>
										) : null}
									</Stack>
								) : null,
							]
								.concat(
									params.archived_only
										? [
												<Stack>
													<Text>{item.deleted_by?.name}</Text>
													{item.deleted_at ? (
														<Box>
															<RelativeTime
																timestamp={item.deleted_at}
																fontSize="sm"
																color="muted"
															/>
														</Box>
													) : null}
												</Stack>,
											]
										: []
								)
								.concat([
									!hasPermission(
										PERMISSIONS.UPLOAD_BULK_TRANSPORT_SERVICES
									) ? null : (
										<Dropdown alignRight key={item.id}>
											<Dropdown.ToggleButton size="sm" level="tertiary">
												<Icons.DotsVertical />
											</Dropdown.ToggleButton>
											<Dropdown.Menu>
												{!item.deleted_at ? (
													<>
														<Component initialState={false}>
															{({ state, setState }) => (
																<>
																	<Dropdown.MenuItem
																		onClick={async () => {
																			setState(true)
																		}}
																	>
																		Edit Cab Type
																	</Dropdown.MenuItem>
																	<Dialog
																		open={state}
																		onClose={() => setState(false)}
																		title="Edit Cab Type"
																		sm
																	>
																		<Dialog.Body>
																			<EditCabTypeTripDestinations
																				cabTypeId={item.id}
																				onSuccess={() => {
																					setState(false)
																					refresh()
																				}}
																				onCancel={() => setState(false)}
																			/>
																		</Dialog.Body>
																	</Dialog>
																</>
															)}
														</Component>
														<Dropdown.MenuItemDivider marginY="0" />
														<Component initialState={false}>
															{({ state, setState }) => (
																<Dropdown.MenuItem
																	color="warning"
																	onClick={async () => {
																		if (state) return
																		if (
																			window.confirm(
																				"Are you sure you want to archive this cab type. Existing data will NOT be affected."
																			)
																		) {
																			setState(true)
																			try {
																				const resp = await XHR(xhr).destroy(
																					item.id
																				)
																				showSnackbar(
																					resp.message ||
																						"Archived successfully"
																				)
																				refresh()
																			} catch (e) {
																				setState(false)
																				const error = e as never as Error
																				window.alert(
																					error.message ||
																						"Something event wrong. Please try after sometime."
																				)
																			}
																		}
																	}}
																>
																	{state ? (
																		"Please wait..."
																	) : (
																		<>
																			<Icons.Trash /> Archive
																		</>
																	)}
																</Dropdown.MenuItem>
															)}
														</Component>
													</>
												) : (
													<Component initialState={false}>
														{({ state, setState }) => (
															<Dropdown.MenuItem
																onClick={async () => {
																	if (state) return
																	if (
																		window.confirm(
																			"Are you sure you want to restore this cab type. Existing data will NOT be affected."
																		)
																	) {
																		setState(true)
																		try {
																			await XHR(xhr).restore(item.id)
																			showSnackbar("Restored successfully")
																			refresh()
																		} catch (e) {
																			setState(false)
																			const error = e as never as Error
																			window.alert(
																				error.message ||
																					"Something event wrong. Please try after sometime."
																			)
																		}
																	}
																}}
															>
																{state ? (
																	"Please wait..."
																) : (
																	<>
																		<Icons.Reply /> Restore
																	</>
																)}
															</Dropdown.MenuItem>
														)}
													</Component>
												)}
											</Dropdown.Menu>
										</Dropdown>
									),
								])
						)}
					/>
				)}
			</ListView>
		</Search>
	)
}

function Filters() {
	const hasPreload = useHasFeatureFlag("ai_generated_content")
	return (
		<Stack gap="4">
			<SelectField
				name="trip_destinations"
				select={SelectTripDestination}
				label="Trip Destinations"
				fetchOnMount
				multiple
				placeholder="Select destination(s)..."
			/>
			<SelectField
				select={Select}
				label="Upload Type"
				name="entry_type"
				options={uploadTypeOptions.filter(
					(option) => hasPreload || option.id !== "only_preload"
				)}
			/>
			<SwitchInputField label="Archived only" name="archived_only" />
		</Stack>
	)
}

export function SelectCabTypes({
	tripDestinations,
	showEntryType = false,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripDestinations?: Array<TTripDestination>
	showEntryType?: boolean
}) {
	const xhr = useXHR()
	return (
		<>
			<AsyncSelect
				multiple
				perFetchLimit={20}
				cacheKey={`cab-types-${(tripDestinations || [])
					.map((t) => t.id)
					.join("-")}`}
				{...props}
				fetch={(q, { page }) =>
					XHR(xhr)
						.get({
							q,
							trip_destinations: tripDestinations?.length
								? tripDestinations.map((t) => t.id)
								: null,
							pagination: "simple",
							page,
							limit: 20,
						})
						.then((resp) => resp.data)
				}
				optionRenderer={({ option, created }) => {
					const bracketRegex = /(\[\[.*?\]\])/
					const parts = option.name.split(bracketRegex)
					return created ? (
						<Box>
							Add "
							{parts.map((part: string, index: number) =>
								bracketRegex.test(part) ? (
									<Text as="span" fontSize="xs" key={index} color="muted">
										{part}
									</Text>
								) : (
									<Text as="span" key={index}>
										{part}
									</Text>
								)
							)}
							"
						</Box>
					) : (
						<Box>
							<Box>
								{option.display_name ? (
									<Box>
										{option.display_name}
										{option.short_name ? (
											<Text fontSize="sm" color="muted">
												[[{option.short_name}]]
											</Text>
										) : null}
									</Box>
								) : (
									<Text fontSize="sm" color="muted">
										[[{option.short_name}]]
									</Text>
								)}
							</Box>
							<ShowCabCapacityInfo
								showEntryType={showEntryType}
								cabType={option}
							/>
						</Box>
					)
				}}
			/>
			{!props.multiple && props.value ? (
				<ShowCabCapacityInfo
					showEntryType={showEntryType}
					cabType={props.value}
				/>
			) : null}
			{props.value?.deleted_at ? (
				<Alert status="warning" inline>
					Cab Type is disabled!
				</Alert>
			) : null}
		</>
	)
}

function EditCabTypeTripDestinations({
	cabTypeId,
	onSuccess,
	onCancel,
}: {
	cabTypeId: number
	onSuccess: () => void
	onCancel: () => void
}) {
	const id = useId()
	const xhr = useXHR()
	const { data } = useSWR(`api/cab-types/${cabTypeId}?${id}`, () =>
		XHR(xhr).show(cabTypeId)
	)
	const validationSchema = validateFormValues(
		Validator.object().shape({
			capacity: EmptyNumberValidator()
				.min(1, "Capacity must be greater than zero")
				.integer("Value must be an integer"), // decimal not allow
			capacity_child_age_limit: EmptyNumberValidator().when("capacity", {
				is: (capacity: number | null) => capacity,
				then: (schema) =>
					schema
						.required("Child Age is required")
						.min(0, "Capacity must be greater than equal to zero")
						.integer("Value must be an integer"),
				otherwise: (schema) => schema.nullable(),
			}),
		})
	)

	if (!data) {
		return <Spinner padding="4" alignCenter />
	}
	const { trip_destinations, capacity, capacity_child_age_limit } = data.data
	return (
		<Form
			validate={validationSchema}
			initialValues={{
				trip_destinations: trip_destinations || [],
				capacity: capacity,
				capacity_child_age_limit: capacity_child_age_limit,
			}}
			onSubmit={withServerErrors(async (values) => {
				if (!values.capacity && values.capacity_child_age_limit) {
					throw new Error("Capacity is required if Child Age is filled.")
				}
				await XHR(xhr).setTripDestinations(
					cabTypeId,
					values.trip_destinations,
					values.capacity || null,
					values.capacity_child_age_limit ?? null
				)
				showSnackbar("Cab Details Updated Successfully")
				onSuccess()
			})}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack gap="4">
						<SelectField
							select={SelectTripDestination}
							name="trip_destinations"
							label="Trip Destinations"
							multiple
						/>
						<Inline gap={"2"}>
							<TextInputField
								label="Total Capacity"
								name={`capacity`}
								type="number"
								placeholder="1"
								min={1}
							/>
							<TextInputField
								label="Child Age"
								name={`capacity_child_age_limit`}
								type="number"
								placeholder="1"
								min={1}
							/>
						</Inline>
						<Divider sm />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Saving..." : "Save Details"}
							</Button>
							<Button
								level="tertiary"
								disabled={submitting}
								onClick={() => onCancel()}
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function SelectFromCabTypeList({
	name,
	children,
	destinations,
	showEntryType = false,
}: {
	name: string
	children: (props: { onOpen: () => void }) => React.ReactNode
	destinations?: Array<TTripDestination>
	showEntryType?: boolean
}) {
	const { value: selectedCabType, onChange: changeCabType } =
		useFieldValue<ICabType>(name)
	const [params, setParams] = useSearch({})
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ onOpen: () => open() })}
			<Dialog open={isOpen} title="Cab Type List" onClose={close}>
				<Dialog.Body>
					<Search
						title="Search Cab Type"
						onSearch={(params) => setParams({ ...params, page: 1 })}
						removeContainerPadding
					>
						<Stack paddingY="4" gap={"4"}>
							<ListView<ICabType, TFilters>
								pageKey="cab-types"
								params={params}
								fetch={(xhr, params) =>
									XHR(xhr).get({
										...params,
										trip_destinations: destinations?.length
											? destinations.map((t) => t.id)
											: null,
										limit: 12,
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								removeContainerPadding
							>
								{({ items: cabTypes }) => {
									return (
										<Stack borderWidth="1" rounded="md" overflow="hidden">
											<Table
												bordered
												responsive
												children={
													<>
														<Box as="thead" backgroundColor={"inset"}>
															<tr>
																<TableHeaderDataCell>Cab</TableHeaderDataCell>
																<TableHeaderDataCell>
																	Total Capacity
																</TableHeaderDataCell>
																<TableHeaderDataCell></TableHeaderDataCell>
															</tr>
														</Box>
														<tbody>
															{(cabTypes || []).map((cab, index) => (
																<Box
																	borderTopWidth={index !== 0 ? "1" : undefined}
																	bgColor={
																		cab.id === selectedCabType.id
																			? "primary"
																			: {
																					hover: "subtle",
																				}
																	}
																	as="tr"
																	key={cab.id}
																	onClick={() => {
																		changeCabType(cab)
																	}}
																	cursor="pointer"
																>
																	<TableDataCell>
																		<ShowCabTypeName cabType={cab} />
																	</TableDataCell>
																	<TableDataCell>
																		<ShowCabCapacityInfo
																			cabType={cab}
																			showEntryType={showEntryType}
																		/>
																	</TableDataCell>
																	<TableDataCell textAlign={"right"}>
																		<RadioInput
																			name={`${name}`}
																			checked={cab.id === selectedCabType?.id}
																			value={cab.id}
																			readOnly
																		/>
																	</TableDataCell>
																</Box>
															))}
														</tbody>
													</>
												}
											/>
										</Stack>
									)
								}}
							</ListView>

							{selectedCabType ? (
								<Inline
									gap={"2"}
									borderWidth="1"
									rounded="md"
									padding={"4"}
									justifyContent="between"
									alignItems={"center"}
									borderLeftWidth={"4"}
								>
									<ShowCabTypeName cabType={selectedCabType} />
									<ShowCabCapacityInfo
										showEntryType={showEntryType}
										cabType={selectedCabType}
									/>
									<Box>
										<Badge success>selected</Badge>
									</Box>
								</Inline>
							) : null}
						</Stack>
					</Search>
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={close}>Done</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function ShowCabTypeName({ cabType }: { cabType: ICabType }) {
	// we are show display name and short name to make the difference between display name and short name
	// instead of showing in name (display name + short name)
	return (
		<>
			{cabType.display_name?.length > 0 ? (
				<Stack flex="1" minWidth="0" gap="px">
					<Text fontWeight="normal">{cabType.display_name}</Text>
					{cabType.short_name ? (
						<Text fontWeight="normal" fontSize="sm" color={"muted"}>
							[[{cabType.short_name}]]
						</Text>
					) : null}
				</Stack>
			) : (
				<Text fontWeight="normal" color={"muted"}>
					{cabType.name.trim()}
				</Text>
			)}
		</>
	)
}
